import React, { useState } from "react";
import {
  PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line, Legend
} from "recharts";

const PALETTE = [
  "#6c5ce7", "#00b894", "#d63031", "#0984e3",
  "#e17055", "#fdcb6e", "#9db1beff"
];

const CategoryChart = ({ data }) => {
  const [chartType, setChartType] = useState("pie");
  const safeData = Array.isArray(data) ? data : [];

  // ✅ Histogram binning
  const histogramData = (() => {
    if (!safeData.length) return [];
    const values = safeData.map((d) => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const binCount = 5;
    const binSize = (max - min) / binCount || 1;
    const bins = Array(binCount).fill(0).map((_, i) => ({
      name: `${Math.round(min + i * binSize)} - ${Math.round(min + (i + 1) * binSize)}`
    }));

    safeData.forEach((d) => {
      const idx = Math.min(Math.floor((d.value - min) / binSize), binCount - 1);
      bins[idx][d.name] = (bins[idx][d.name] || 0) + d.value;
    });

    return bins;
  })();

  const chartWidth = 400;
  const chartHeight = 260;

  return (
    <React.Fragment>
      <div className="card chart-card">
        <div
          className="card-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3>Spending by Category</h3>
        <div className="chart-toggle">
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            style={{
              borderRadius: "8px",
              border: "1px solid #ccc",
              fontSize: "0.9rem",
              padding: "6px 10px",
            }}
          >
            <option value="pie">Pie</option>
            <option value="bar">Bar</option>
            <option value="line">Line</option>
            <option value="histogram">Histogram</option>
          </select>
        </div>
      </div>

      <div
        className="card-body"
        style={{
          width: "100%",
          height: chartHeight,
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {safeData.length === 0 ? (
          <p style={{ color: "#999" }}>No expenses yet</p>
        ) : chartType === "pie" ? (
          // 🥧 PIE CHART
          <PieChart width={chartWidth} height={chartHeight}>
            <Pie
              data={safeData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
            >
              {safeData.map((_, idx) => (
                <Cell key={idx} fill={PALETTE[idx % PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        ) : chartType === "bar" ? (
          // 📊 BAR CHART
          <BarChart width={chartWidth} height={chartHeight} data={safeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: "#555", fontSize: 12 }}
            interval={0}
            angle={-25}
            textAnchor="end"
            />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value">
              {safeData.map((_, idx) => (
                <Cell key={idx} fill={PALETTE[idx % PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        ) : chartType === "line" ? (
          // 📈 LINE CHART (dots colored per category)
          <LineChart width={chartWidth} height={chartHeight} data={safeData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 12 }}
              interval={0}
              angle={-25}
              textAnchor="end"
            />
            <YAxis />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#e75cadff"
              strokeWidth={2}
              dot={({ cx, cy, index }) => (
                <circle
                  cx={cx}
                  cy={cy}
                  r={6}
                  fill={PALETTE[index % PALETTE.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              )}
              activeDot={{ r: 8 }}
              connectNulls
            />
          </LineChart>
        ) : (
          // 📉 HISTOGRAM
          <BarChart width={chartWidth} height={chartHeight} data={histogramData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              label={{ value: "Expense Range", position: "insideBottom", dy: 10 }}
            />
            <YAxis
              label={{ value: "Total Amount", angle: -90, position: "insideLeft" }}
            />
            <Tooltip />
            <Legend />
            {safeData.map((cat, idx) => (
              <Bar
                key={cat.name}
                dataKey={cat.name}
                stackId="a"
                fill={PALETTE[idx % PALETTE.length]}
                barSize={18}
              />
            ))}
          </BarChart>
        )}
      </div>
    </div>
  </React.Fragment>
  );
};

export default CategoryChart;

